// Utilitários de fuso horário — tudo no horário oficial de Brasília.
// Evita que o navegador/servidor (UTC ou outro fuso) grave/exiba horários trocados.

export const BR_TZ = "America/Sao_Paulo";

const partsFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: BR_TZ,
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  weekday: "short",
});

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function toDate(d: Date | string | number): Date {
  return d instanceof Date ? d : new Date(d);
}

export function brParts(d: Date | string | number) {
  const map: Record<string, string> = {};
  for (const p of partsFmt.formatToParts(toDate(d))) map[p.type] = p.value;
  const hour = Number(map.hour) % 24;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour,
    minute: Number(map.minute),
    second: Number(map.second),
    dow: WEEKDAYS[map.weekday ?? "Sun"] ?? 0,
  };
}

/** "YYYY-MM-DD" no fuso de Brasília. */
export function brIsoDate(d: Date | string | number): string {
  const p = brParts(d);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** Minutos desde a meia-noite (Brasília). */
export function brMinutes(d: Date | string | number): number {
  const p = brParts(d);
  return p.hour * 60 + p.minute;
}

/** Dia da semana (0=Dom) em Brasília. */
export function brDow(d: Date | string | number): number {
  return brParts(d).dow;
}

/** "HH:MM" em Brasília. */
export function brTime(d: Date | string | number): string {
  const p = brParts(d);
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
}

/** "DD/MM/AAAA" em Brasília. */
export function brDateLabel(d: Date | string | number): string {
  const p = brParts(d);
  return `${String(p.day).padStart(2, "0")}/${String(p.month).padStart(2, "0")}/${p.year}`;
}

function tzOffsetMs(utc: Date) {
  const p = brParts(utc);
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUTC - utc.getTime();
}

/**
 * Converte uma data ("YYYY-MM-DD") + minutos do dia, interpretados no horário de
 * Brasília, para um instante ISO (UTC) — formato usado no banco.
 */
export function brIsoFrom(dateIso: string, minutes: number): string {
  const [y, m, d] = dateIso.slice(0, 10).split("-").map(Number);
  const naive = Date.UTC(y!, (m ?? 1) - 1, d ?? 1, Math.floor(minutes / 60), minutes % 60, 0, 0);
  let ts = naive - tzOffsetMs(new Date(naive));
  ts = naive - tzOffsetMs(new Date(ts));
  return new Date(ts).toISOString();
}

/** "Âncora" de um dia: meio-dia UTC (09:00 em Brasília) — seguro para aritmética de datas. */
export function brDayAnchor(dateIso: string): Date {
  return new Date(`${dateIso.slice(0, 10)}T12:00:00.000Z`);
}

/** Data de hoje ("YYYY-MM-DD") em Brasília. */
export function brToday(): string {
  return brIsoDate(new Date());
}

/** Âncora do domingo da semana que contém a data informada. */
export function brStartOfWeek(dateIso: string): Date {
  const a = brDayAnchor(dateIso);
  a.setUTCDate(a.getUTCDate() - a.getUTCDay());
  return a;
}

/** Soma dias a uma âncora (mantendo meio-dia UTC). */
export function addAnchorDays(anchor: Date, days: number): Date {
  const x = new Date(anchor);
  x.setUTCDate(x.getUTCDate() + days);
  return x;
}

/** "YYYY-MM-DD" de uma âncora. */
export function anchorIso(anchor: Date): string {
  return anchor.toISOString().slice(0, 10);
}
